import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import Marquee from "@/components/Marquee";
import MineralCard from "@/components/MineralCard";
import { LineReveal, Reveal, Overline, EASE } from "@/components/motion";
import { MINERALS, MANIFESTO, STATS, CONTACT, waLink } from "@/data/site";

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.3, delay: 0.9, ease: EASE }}
      className="relative hidden lg:block"
      style={{ perspective: 1200 }}
      data-testid="hero-visual"
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => { mx.set(0); my.set(0); }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative"
      >
        <div className="relative overflow-hidden rounded-[2rem] border border-gold/25 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.8)]">
          <img src="/assets/img/gold.jpg" alt="Gold-bearing ore under spotlight" className="h-[520px] w-full object-cover" />
          <div className="absolute inset-0 spotlight" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-transparent to-navy-950/30" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between" style={{ transform: "translateZ(40px)" }}>
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-gold">Parcel 001</p>
              <p className="mt-1 font-serif text-xl text-slate-50">Assay-verified gold</p>
            </div>
            <p className="font-serif text-3xl gold-text">Au</p>
          </div>
        </div>
        <div
          className="absolute -left-10 -top-10 flex h-28 w-28 items-center justify-center rounded-2xl border border-gold/30 bg-navy-900/80 backdrop-blur-xl shadow-[0_24px_60px_-20px_rgba(212,175,55,0.4)]"
          style={{ transform: "translateZ(70px)" }}
        >
          <img src="/assets/tg-icon.png" alt="Tuskers Grey icon" className="h-16 w-auto" />
        </div>
      </motion.div>
    </motion.div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0 -bottom-56">
        <img src="/assets/img/hero-bg.jpg" alt="" className="h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/85 to-navy-950" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28 lg:px-10">
        <div className="grid items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            >
              <Overline data-testid="hero-overline">Mining & mine product brokerage — Midlands, Zimbabwe</Overline>
            </motion.div>
            <h1 className="mt-7 font-serif text-4xl leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              <LineReveal delay={0.3}>From the Great Dyke</LineReveal>
              <LineReveal delay={0.44}>
                to <em className="gold-text not-italic">global markets,</em>
              </LineReveal>
              <LineReveal delay={0.58}>with proof in hand.</LineReveal>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
              data-testid="hero-subtext"
            >
              Tuskers Grey Investments brokers gold, chrome, lithium, PGMs, nickel and iron ore —
              every parcel sampled, assayed and documented before it leaves the Midlands.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-explore-button"
                className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-navy-950 transition-all duration-300 hover:bg-gold-light hover:shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
              >
                Explore minerals
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={waLink(CONTACT.phones[1].wa, "Hello Tuskers Grey Investments — I'd like to discuss minerals.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.25em] text-slate-200 transition-all duration-300 hover:border-gold/60 hover:text-gold"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp us
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const ctaRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ctaRef, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-80, 80]);

  return (
    <main data-testid="home-page">
      <Hero />

      <Marquee items={["Gold", "Chrome", "Lithium", "PGMs", "Nickel", "Iron Ore", "Midlands Province", "Assay-Verified", "Export-Ready"]} />

      <section data-testid="manifesto-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
        <Reveal>
          <Overline>The manifesto</Overline>
          <h2 className="mt-5 max-w-3xl font-serif text-3xl leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Three promises every parcel travels with.
          </h2>
        </Reveal>
        <div className="mt-20 space-y-0">
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.n} delay={i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${ch.n}`}
                className="group grid gap-6 border-t hairline-gold py-12 lg:grid-cols-12 lg:items-baseline lg:py-16"
              >
                <span className="font-serif text-5xl text-gold/30 transition-colors duration-500 group-hover:text-gold lg:col-span-2 lg:text-6xl">
                  {ch.n}
                </span>
                <h3 className="font-serif text-2xl text-slate-100 sm:text-3xl lg:col-span-4">{ch.title}</h3>
                <p className="max-w-xl text-base leading-relaxed text-slate-400 lg:col-span-6">{ch.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="featured-minerals" className="mx-auto max-w-7xl px-6 pb-28 lg:px-10 lg:pb-40">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>What we move</Overline>
            <h2 className="mt-5 font-serif text-3xl tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Six minerals. One standard.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="featured-view-all"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:text-gold-light"
          >
            View all minerals <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MINERALS.slice(0, 3).map((m, i) => (
            <MineralCard key={m.id} mineral={m} index={i} />
          ))}
        </div>
      </section>

      <section data-testid="stats-band" className="border-y hairline-gold bg-navy-900/50">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-12 px-6 py-16 lg:grid-cols-4 lg:px-10 lg:py-20">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div data-testid={`stat-${i}`} className="pr-6">
                <p className="font-serif text-4xl gold-text sm:text-5xl">{s.value}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section ref={ctaRef} data-testid="home-cta" className="relative overflow-hidden">
        <motion.div style={{ y: yImg }} className="absolute inset-0 -top-24 -bottom-24">
          <img src="/assets/img/zimbabwe.jpg" alt="Zimbabwe savanna at dusk" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy-950/80" />
        </motion.div>
        <div className="relative mx-auto max-w-7xl px-6 py-32 lg:px-10 lg:py-44">
          <Reveal>
            <Overline>Ready when you are</Overline>
            <h2 className="mt-5 max-w-3xl font-serif text-3xl leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Buying minerals, or holding a parcel? <em className="gold-text not-italic">Let's talk.</em>
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={CONTACT.phones[1].tel}
                data-testid="cta-call-button"
                className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-navy-950 transition-all duration-300 hover:bg-gold-light"
              >
                Call {CONTACT.phones[1].label}
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 text-xs uppercase tracking-[0.25em] text-slate-100 transition-all duration-300 hover:border-gold/60 hover:text-gold"
              >
                All contact options <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
